import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, XCircle, BadgeCheck, FileDown, Clock } from "lucide-react";
import { toast } from "sonner";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}
function formatDateIT(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function VersamentiPendingCard() {
  const [data, setData] = useState({ rows: [], count: 0, totale: 0 });
  const [loading, setLoading] = useState(true);
  const [reject, setReject] = useState(null); // versamento being rejected
  const [rejectNote, setRejectNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/versamenti-pending");
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const validate = async (v) => {
    if (!window.confirm(`Confermi la convalida del versamento di ${v.collaboratore?.nome || ''} ${v.collaboratore?.cognome || ''} di ${formatEUR(v.totale)}?`)) return;
    try {
      await api.post(`/versamenti/${v.id}/validate`, { note: "" });
      toast.success("Versamento convalidato");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadPdf = async (id, dataD) => {
    try {
      const res = await api.get(`/versamenti/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `versamento_${dataD}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitReject = async () => {
    if (!rejectNote.trim()) { toast.error("Inserisci la motivazione del rifiuto"); return; }
    setSubmitting(true);
    try {
      await api.post(`/versamenti/${reject.id}/reject`, { note: rejectNote.trim() });
      toast.success("Versamento rifiutato. Polizze tornate disponibili.");
      setReject(null);
      setRejectNote("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (loading) return null;
  if (data.count === 0) return null;

  return (
    <>
      <Card className="p-6 border-amber-300 bg-amber-50/40" data-testid="pending-versamenti-card">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Versamenti da convalidare
            </div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">{data.count} versament{data.count === 1 ? "o" : "i"} in attesa</h2>
            <p className="text-sm text-slate-600 mt-1">
              Verifica l'effettivo accredito sul conto/in agenzia e conferma. Se l'importo non corrisponde, rifiuta indicando la motivazione.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase text-amber-700 font-semibold tracking-wider">Totale in attesa</div>
            <div className="font-heading text-2xl font-bold text-amber-700 font-mono-num">{formatEUR(data.totale)}</div>
          </div>
        </div>

        <div className="overflow-x-auto bg-white rounded-lg border border-amber-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-amber-50 text-left">
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-amber-800">Collaboratore</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-amber-800">Data versamento</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-amber-800">Destinazione</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-amber-800 text-right">Polizze</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-amber-800 text-right">Totale</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-amber-800 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-100">
              {data.rows.map((v) => (
                <tr key={v.id} className="hover:bg-amber-50/50" data-testid={`pending-row-${v.id}`}>
                  <td className="px-3 py-3">
                    <div className="font-medium text-slate-900">
                      {v.collaboratore?.nome || ""} {v.collaboratore?.cognome || ""}
                    </div>
                    {v.note && <div className="text-xs text-slate-500 italic mt-0.5">"{v.note}"</div>}
                  </td>
                  <td className="px-3 py-3">{formatDateIT(v.data_versamento)}</td>
                  <td className="px-3 py-3">
                    <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700">
                      {v.destinazione === "conto_corrente" ? "C/C agenzia" : "In agenzia"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right font-mono-num">{v.n_polizze}</td>
                  <td className="px-3 py-3 text-right font-mono-num font-bold text-slate-900">{formatEUR(v.totale)}</td>
                  <td className="px-3 py-3 text-right">
                    <Button size="sm" variant="outline" className="mr-1 h-7 px-2"
                            onClick={() => downloadPdf(v.id, v.data_versamento)}
                            data-testid={`pending-pdf-${v.id}`}>
                      <FileDown className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="mr-1 h-7 px-2 border-red-300 text-red-700 hover:bg-red-50"
                            onClick={() => { setReject(v); setRejectNote(""); }}
                            data-testid={`pending-reject-${v.id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Rifiuta
                    </Button>
                    <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => validate(v)}
                            data-testid={`pending-validate-${v.id}`}>
                      <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Convalida
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!reject} onOpenChange={(o) => { if (!o) setReject(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Rifiuta versamento</DialogTitle>
          </DialogHeader>
          {reject && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                Stai per rifiutare il versamento di <b>{reject.collaboratore?.nome} {reject.collaboratore?.cognome}</b> di <b>{formatEUR(reject.totale)}</b> ({reject.n_polizze} polizze).
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                Le {reject.n_polizze} polizze torneranno disponibili per un nuovo versamento. Il collaboratore riceverà una notifica via email con la motivazione.
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Motivazione del rifiuto*</label>
                <Textarea
                  className="mt-1"
                  rows={3}
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  placeholder="Es. Importo non corrispondente, manca documentazione…"
                  data-testid="reject-note-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReject(null)}>Annulla</Button>
            <Button onClick={submitReject} disabled={submitting || !rejectNote.trim()}
                    className="bg-red-600 hover:bg-red-700 text-white" data-testid="reject-confirm">
              <XCircle className="w-4 h-4 mr-1" /> {submitting ? "Rifiuto..." : "Rifiuta versamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
